// ============================================================
// Slingshot SEO — Homepage Refresh Demo
// All copy, imagery and content in one place (designer spec)
// ============================================================
import {
  Search,
  Sparkles,
  MousePointerClick,
  Repeat,
  TrendingUp,
  Link2,
  Trophy,
  Gauge,
  Layers,
  ShieldCheck,
  GraduationCap,
  Rocket,
  Workflow,
  BarChart3,
  Scale,
  HeartPulse,
  Hammer,
  ShoppingCart,
  Store,
  FileText,
  MapPin,
  Radar,
  Target,
} from "lucide-react";

export const FOUNDING_YEAR = 2006;
export const YEARS_IN_MARKET = new Date().getFullYear() - FOUNDING_YEAR;

// ---------- Locked brand assets (pulled from the live site) ----------
export const ASSETS = {
  logo: "https://www.slingshotseo.com/wp-content/uploads/2018/01/logo.png",
  badgeRecommended:
    "https://www.slingshotseo.com/wp-content/uploads/2019/08/recommended-company.png",
  badgeInc500:
    "https://www.slingshotseo.com/wp-content/uploads/2019/08/inc-500-list-300x300.png",
  awardsStrip: "https://www.slingshotseo.com/wp-content/uploads/2017/12/Awards.png",
};

export const NAV_LINKS = [
  { label: "About", href: "https://www.slingshotseo.com/about/", internal: "/about", testId: "header-nav-about-link" },
  { label: "Services", href: "https://www.slingshotseo.com/services/", internal: "/services", testId: "header-nav-services-link" },
  { label: "Solution", href: "https://www.slingshotseo.com/solution/", internal: "/solution", testId: "header-nav-solution-link" },
  { label: "Blog", href: "https://www.slingshotseo.com/blog/", internal: "/blog", testId: "header-nav-blog-link" },
  { label: "Contact", href: "https://www.slingshotseo.com/contact/", internal: "/contact", testId: "header-nav-contact-link" },
];

// Industries dropdown (replicates homepage vertical markets; rendered after "Services" in the header)
export const INDUSTRIES_MENU = [
  { label: "Legal", href: "/legal-marketing-machine", testId: "nav-industry-legal" },
  { label: "Healthcare", href: "/healthcare-marketing-machine", testId: "nav-industry-healthcare" },
  { label: "Home Services", href: "/home-services-marketing-machine", testId: "nav-industry-home-services" },
  { label: "E-Commerce", href: "/ecommerce-marketing-machine", testId: "nav-industry-ecommerce" },
  { label: "Insurance Services", href: "/insurance-marketing-machine", testId: "nav-industry-insurance" },
  { label: "Education", href: "/education-marketing-machine", testId: "nav-industry-education" },
];

export const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/pg/slingshotseo/about/",
  twitter: "https://twitter.com/slingshotseo?lang=en",
  linkedin: "https://www.linkedin.com/company/27161257",
};

// ---------- Hero rotator (3–4 slides, will be replaced by Flatsome rotator) ----------
export const HERO_SLIDES = [
  {
    key: "flagship",
    eyebrow: "Award-Winning Search Marketing · Since 2006",
    titlePre: "Be Found ",
    titleAccent: "Everywhere",
    titlePost: " Search Happens",
    subline:
      "Google. ChatGPT. AI Overviews. Maps. We engineer visibility across every result that matters — then turn it into pipeline and revenue.",
    ctaPrimary: "Get My Free Visibility Audit",
    ctaSecondary: "See How The Machine Works",
    ctaSecondaryTarget: "#imm",
    bg: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1920&auto=format&fit=crop",
  },
  {
    key: "imm",
    eyebrow: "New · Rapid-Deployment Growth System",
    titlePre: "The Web Marketing ",
    titleAccent: "Machine™",
    titlePost: "",
    subline:
      "Pre-engineered frameworks, automation, and industry playbooks that launch in days — not quarters. SEO, GEO, PPC and Retargeting as one machine.",
    ctaPrimary: "Get My Free Visibility Audit",
    ctaSecondary: "Explore Industry Flavors",
    ctaSecondaryTarget: "#imm",
    bg: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1920&auto=format&fit=crop",
  },
  {
    key: "geo",
    eyebrow: "Generative Engine Optimization (GEO)",
    titlePre: "Rank in Google. ",
    titleAccent: "Get Cited by AI.",
    titlePost: "",
    subline:
      "Our GEO practice gets your brand cited inside ChatGPT, Perplexity and Google AI Overviews — while Foundational SEO™ keeps you winning Page 1.",
    ctaPrimary: "Get My Free Visibility Audit",
    ctaSecondary: "See How The Machine Works",
    ctaSecondaryTarget: "#imm",
    bg: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=1920&auto=format&fit=crop",
  },
  {
    key: "ppc",
    eyebrow: "Advanced PPC + Precision Retargeting",
    titlePre: "SEO Builds. PPC Accelerates. ",
    titleAccent: "Retargeting Converts.",
    titlePost: "",
    subline:
      "Capture high-intent demand today and re-engage the 97% who don't convert — paid media engineered to amplify every organic gain.",
    ctaPrimary: "Get My Free Visibility Audit",
    ctaSecondary: "See How The Machine Works",
    ctaSecondaryTarget: "#imm",
    bg: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1920&auto=format&fit=crop",
  },
];

// ---------- Icon-box services band (6 boxes, modern line icons) ----------
export const ICON_BOX_SERVICES = [
  {
    key: "foundational-seo",
    icon: Search,
    title: "Foundational SEO™",
    body: "The technical bedrock: fast, secure, crawlable, mobile-flawless websites that search engines — and AI models — trust.",
    testId: "services-iconbox-foundational-seo",
  },
  {
    key: "geo",
    icon: Sparkles,
    title: "Generative Engine Optimization",
    body: "Entities, answers and authority engineered so ChatGPT, Perplexity and Google AI Overviews cite your brand — not your competitor's.",
    testId: "services-iconbox-geo",
  },
  {
    key: "advanced-ppc",
    icon: MousePointerClick,
    title: "Advanced PPC Management",
    body: "High-intent paid search with engineered landing pages and bid automation — capturing demand today while organic compounds.",
    testId: "services-iconbox-advanced-ppc",
  },
  {
    key: "retargeting",
    icon: Repeat,
    title: "Precision Retargeting",
    body: "Segmented remarketing across display, social and video that turns the 97% of visitors who leave into pipeline.",
    testId: "services-iconbox-retargeting",
  },
  {
    key: "cro",
    icon: TrendingUp,
    title: "Conversion Optimization",
    body: "A/B testing, heat-mapping and funnel analysis that squeeze more leads and sales from every visit you already earn.",
    testId: "services-iconbox-cro",
  },
  {
    key: "link-building",
    icon: Link2,
    title: "White Hat, Premium Link Building",
    body: "Contextually relevant, permanent placements that build a natural, zero-toxicity authority profile algorithms and AI reward.",
    testId: "services-iconbox-link-building",
  },
];

// ---------- Lead form panel ----------
export const LEAD_FORM = {
  heading: "Free Visibility Audit",
  sub: "See exactly where you stand in Google, AI search and paid media — and the fastest path to more leads.",
  submitLabel: "Get My Free Audit",
  note: "*We will get back to you in 2 business days or less!",
};

// ---------- About / philosophy (refresh of the long-form section) ----------
export const ABOUT = {
  intro:
    "Slingshot SEO is a full-service search marketing practice. Since 2006, our strategic consulting and hands-on execution have delivered unparalleled results for enterprise-caliber clients — across Google, AI engines and paid media.",
  block1: {
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1200&auto=format&fit=crop",
    paragraphs: [
      "Search has changed. Your customers don't just Google anymore — they ask ChatGPT, scan AI Overviews, compare options in the map pack, and click ads long before they ever reach your website. Winning today means being present, credible and persuasive at every one of those moments.",
      "Our delivery model is built on the same CLASS framework that made us an Inc. 500 company — Content, Links, Architecture, Social and Strategy — now supercharged for the AI era with entity optimization, answer-ready content and structured data that generative engines can trust and cite.",
    ],
  },
  block2: {
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop",
    paragraphs: [
      "But visibility without conversion is just noise. That's why advanced PPC captures high-intent demand the moment it appears, and precision retargeting brings back the 97% of visitors who leave without acting. Organic builds the asset; paid compounds the return.",
      "The result is one accountable growth system — strategy, execution and reporting tied to leads and sales, not vanity metrics. Experience what a modern, full-service search partner can do for your company.",
    ],
  },
};

// ---------- Dark outcomes section ----------
export const OUTCOMES = {
  heading: "Increase Traffic, Leads and Sales",
  bg: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=1920&auto=format&fit=crop",
  bullets: [
    { icon: Trophy, text: "Own Page 1 — and the AI answers above it" },
    { icon: Gauge, text: "Reach results faster with our StrikingDistance™ methodology" },
    { icon: Layers, text: "Full-funnel coverage: SEO + GEO + PPC + Retargeting" },
    { icon: ShieldCheck, text: "Tracking you can trust — every dollar tied to revenue" },
    { icon: GraduationCap, text: "US-based marketing professionals with MBAs and certifications" },
  ],
  scorecard: {
    title: "Visibility Scorecard",
    caption: "Sample audit output — every engagement starts with one.",
    rows: [
      { label: "Organic Search Visibility", value: 86, display: "Strong" },
      { label: "AI Citations · ChatGPT / Perplexity / AI Overviews", value: 64, display: "Growing" },
      { label: "Paid Search Efficiency (PPC)", value: 78, display: "Optimized" },
      { label: "Retargeting Conversion Lift", value: 71, display: "Compounding" },
    ],
  },
};

// ---------- The Web Marketing Machine™ ----------
export const IMM = {
  eyebrow: "New · From Slingshot SEO",
  heading: "The Web Marketing Machine™",
  sub: "Stop buying marketing piecemeal. The Machine is our rapid-deployment growth system — pre-engineered frameworks, automation and industry playbooks that launch in days, not quarters.",
  features: [
    {
      icon: Rocket,
      title: "Rapid Deployment",
      body: "Launch-ready frameworks with schema, FAQ modules and content rotators built in — live in days, not quarters.",
    },
    {
      icon: Workflow,
      title: "Automation at the Core",
      body: "Lead routing, review generation, reporting and campaign optimization run on autopilot — your team stays focused on closing.",
    },
    {
      icon: Layers,
      title: "Every Channel, One System",
      body: "Foundational SEO™, GEO, advanced PPC and Retargeting engineered to work as a single, accountable growth engine.",
    },
    {
      icon: BarChart3,
      title: "Revenue-Tied Reporting",
      body: "Dashboards that connect visibility to leads and sales — never vanity metrics.",
    },
  ],
  engines: [
    { icon: Search, label: "SEO Engine" },
    { icon: Sparkles, label: "GEO Engine" },
    { icon: MousePointerClick, label: "PPC Engine" },
    { icon: Repeat, label: "Retargeting Engine" },
  ],
  industries: [
    {
      key: "legal",
      icon: Scale,
      name: "Legal",
      line: "Case-driving visibility for firms in competitive markets.",
      href: "/legal-marketing-machine",
      testId: "imm-industry-card-legal",
    },
    {
      key: "healthcare",
      icon: HeartPulse,
      name: "Healthcare",
      line: "Compliant patient acquisition for practices and systems.",
      href: "/healthcare-marketing-machine",
      testId: "imm-industry-card-healthcare",
    },
    {
      key: "home-services",
      icon: Hammer,
      name: "Home Services",
      line: "Own the map pack and the phone in every service area.",
      href: "/home-services-marketing-machine",
      testId: "imm-industry-card-home-services",
    },
    {
      key: "ecommerce",
      icon: ShoppingCart,
      name: "E-Commerce",
      line: "Product visibility from search to shopping to retargeting.",
      href: "/ecommerce-marketing-machine",
      testId: "imm-industry-card-ecommerce",
    },
    {
      key: "insurance",
      icon: ShieldCheck,
      name: "Insurance Services",
      line: "Policyholder acquisition and trust-first visibility for agencies and carriers.",
      href: "/insurance-marketing-machine",
      testId: "imm-industry-card-insurance",
    },
    {
      key: "education",
      icon: GraduationCap,
      name: "Education",
      line: "Enrollment-driving visibility for schools, universities and edtech.",
      href: "/education-marketing-machine",
      testId: "imm-industry-card-education",
    },
  ],
};

// ---------- Recipe grid (8 cards) ----------
export const RECIPE_HEADING = "Our recipe for consistent search results";
export const RECIPE_CARDS = [
  {
    icon: ShieldCheck,
    title: "Foundational SEO™",
    items: [
      "Mobile-responsiveness compliance",
      "Core Web Vitals & site speed",
      "Site-wide SSL & layered security",
      "Clean, crawlable architecture",
      "24/7 uptime management",
    ],
  },
  {
    icon: Sparkles,
    title: "GEO & AI Search",
    items: [
      "AI Overview optimization",
      "Entity & knowledge graph building",
      "Answer-ready content engineering",
      "AI crawler readiness (llms.txt)",
      "Citation tracking across AI engines",
    ],
  },
  {
    icon: Target,
    title: "Advanced PPC",
    items: [
      "High-intent keyword bidding",
      "Performance Max & Shopping",
      "Landing page engineering",
      "Conversion tracking & attribution",
      "Budget & bid automation",
    ],
  },
  {
    icon: Repeat,
    title: "Retargeting & Remarketing",
    items: [
      "Audience segmentation",
      "Display, social & video sequencing",
      "Dynamic product retargeting",
      "Creative testing at scale",
      "Frequency & fatigue management",
    ],
  },
  {
    icon: FileText,
    title: "Content & Schema",
    items: [
      "Keyword-rich, answer-first copy",
      "Schema & structured data",
      "FAQ modules & rich results",
      "Blog strategy & digital PR",
      "Content rotators",
    ],
  },
  {
    icon: MapPin,
    title: "Local Search Domination",
    items: [
      "Google Business Profile management",
      "Map pack optimization",
      "Location page build-out",
      "Review generation strategy",
      "Local keyword strategy",
    ],
  },
  {
    icon: TrendingUp,
    title: "Conversion Optimization",
    items: [
      "A/B & multivariate testing",
      "Heat-mapping & session replay",
      "Funnel analysis",
      "Form & CTA optimization",
      "Landing page experimentation",
    ],
  },
  {
    icon: Radar,
    title: "Competitive Intelligence",
    items: [
      "Gap link analysis",
      "Competitor ad monitoring",
      "Keyword opportunity mining",
      "AI citation share tracking",
      "Organic rank monitoring",
    ],
  },
];

// ---------- Stats band (updated per client) ----------
export const STATS_BG =
  "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1920&auto=format&fit=crop";
export const STATS = [
  { key: "clients", label: "Clients", value: 887, suffix: "+", testId: "stats-clients" },
  { key: "websites", label: "Optimized Websites", value: 751, suffix: "", testId: "stats-optimized-websites" },
  { key: "satisfaction", label: "Client Satisfaction", value: 98, suffix: "%", testId: "stats-satisfaction" },
  { key: "years", label: "Years of Service", value: YEARS_IN_MARKET, suffix: "", testId: "stats-years" },
];

// ---------- FAQ (also powers FAQPage schema) ----------
export const FAQ_HEADING = "Straight answers to modern search questions";
export const FAQ_ITEMS = [
  {
    q: "What is GEO (Generative Engine Optimization)?",
    a: "GEO is the practice of optimizing your brand to appear inside AI-generated answers — ChatGPT, Perplexity and Google AI Overviews. It complements Foundational SEO™ by strengthening entities, authority signals and answer-ready content so generative engines trust and cite you.",
  },
  {
    q: "Is GEO replacing SEO?",
    a: "No — GEO builds on SEO. AI engines source their answers from crawlable, authoritative, well-structured websites. Foundational SEO™ earns that trust; GEO makes sure the AI layer above the results page mentions your brand by name.",
  },
  {
    q: "How do PPC and Retargeting supplement SEO?",
    a: "PPC captures high-intent demand immediately while organic rankings compound. Retargeting re-engages the 97% of visitors who leave without converting. Together they turn every organic gain into more pipeline — one accountable system, one growth number.",
  },
  {
    q: "What is The Web Marketing Machine™?",
    a: "It's our rapid-deployment delivery system: pre-engineered site frameworks, schema and FAQ modules, content rotators, automation and reporting — tuned with industry flavors for Legal, Healthcare, Home Services, E-Commerce, Insurance and Education businesses.",
  },
  {
    q: "What's included in the Free Visibility Audit?",
    a: "A scorecard of where you stand in Google rankings, AI citations, paid search efficiency and retargeting readiness — plus the prioritized, fastest path to more leads based on your market. Delivered by a US-based strategist in 2 business days or less.",
  },
  {
    q: "How fast will I see results?",
    a: "PPC and Retargeting can show impact within weeks. SEO and GEO build compounding gains — and our StrikingDistance™ methodology targets the terms you can win fastest first. Your audit outlines the exact sequence for your market.",
  },
];
